import { Box, useBoolean } from '@chakra-ui/react'
import {
  ContentNode,
  ContentNodeUtils,
  FieldContentNode,
  ManualContentHelpers,
} from '@helo/cms'
import { ReactNode } from 'react'

import { ErrorBoundary } from '../../../components/ErrorBoundary/ErrorBoundary'
import { WidgetError } from '../../../components/Errors/WidgetError'
import { CellRendererProps } from '../table.types'
import { DateCell } from './DateCell'
import { ListCell } from './ListCell'
import { NumberCell } from './NumberCell'
import { StringCell } from './StringCell'
import { TypeRendererCell } from './TypeRendererCell'

type CellRendererCompProps = CellRendererProps
/**
 * Compose with cell component to wrap cell with common component.
 *
 * Adds full alignment to every kind of cell, no matter the content type.
 *
 * Usage:
 * ```tsx
 * {
 *  component: CellWrapper(TextCell),
 * }
 * ```
 */
export const CellRenderer = ({ colDef, ...props }: CellRendererCompProps) => {
  const [isCellHover, setIsCellHover] = useBoolean(false)
  const columnConfig = colDef.context?.tableColumnConfig

  if (!columnConfig) {
    throw new Error('Column config not found on colDef')
  }

  const columnDataTypeConfig = columnConfig.config

  if (!columnDataTypeConfig) {
    throw new Error('Column data type config not found on column config')
  }

  const rowContentNode = props.data as ContentNode

  let contentNode: ContentNode | null = null

  contentNode =
    ContentNodeUtils.findContentNode(columnConfig.selector)(rowContentNode) ??
    ManualContentHelpers.createContentNode(columnDataTypeConfig, null)

  const { key: columnConfigKey, ...columnConfigRest } =
    columnConfig.columnConfig

  contentNode = ContentNodeUtils.setState([columnConfigRest] as any)(
    contentNode,
  )

  if (!contentNode) {
    throw new Error(`Content node not found for column config}`)
  }

  const isField = contentNode.kind === 'FIELD'

  // hack override for tables. TODO: figure out better way to handle this override.
  contentNode.state.size = 'small'
  if (isField) {
    contentNode.ofType.state.size = 'small'
  }

  const typeName = isField
    ? contentNode.ofType.typeName
    : columnDataTypeConfig.typeName
  const CellComponent = cellDataTypeMap[typeName] ?? TypeRendererCell

  if (!CellComponent) {
    throw new Error(
      `CellRenderer: No cell component found for dataType: ${typeName}`,
    )
  }

  return (
    <Box
      padding="12px 0"
      lineHeight="1.2"
      flex="1"
      onMouseEnter={setIsCellHover.on}
      onMouseLeave={setIsCellHover.off}
      data-component="CellRenderer"
    >
      <ErrorBoundary
        renderError={error => (
          <WidgetError flexDir="row">{error.message}</WidgetError>
        )}
      >
        <CellComponent
          // overflowWrap="break-word"
          isCellHover={isCellHover}
          {...props}
          // colDef={{ ...colDef, options: mappedOptions }}
          contentNode={contentNode}
        />
      </ErrorBoundary>
    </Box>
  )
}

const cellDataTypeMap: Record<
  string,
  (props: CellRendererProps & { contentNode: FieldContentNode }) => ReactNode
> = {
  String: StringCell,
  Int: NumberCell,
  Float: NumberCell,
  ID: StringCell,
  date: DateCell,
  DateTime: StringCell,
  number: NumberCell,
  // Helo2Place: Helo2PlaceCell,
  // Helo2ServiceType: Helo2ServiceTypeCell,
  List: ListCell,
  Boolean: StringCell,
  TypeRenderer: TypeRendererCell,
  // Place: PlaceCell,
  // TO convert:
  // deltaTrend: DeltaTrendCell,
  // file: DownloadCell,
  // currency: CurrencyCell,
  // place: PlaceCell,
  // tag: TagsCell,
  // tags: TagsCell,
  // servicetype: ServiceTypeCell,
  // array: TableCell,
  // object: JsonCell,
}
